package dev.terrakok.mapitan.domain.geojson

import androidx.compose.ui.graphics.Color
import dev.terrakok.mapitan.domain.*
import dev.terrakok.mapitan.domain.utils.toHexRgb
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.spatialk.geojson.*

data class TrackDraft(
    val points: List<TrackPoint>,
    val color: Color,
    val lineThickness: Int,
    val lineStyle: LineStyle,
)

data class TrackPointDrag(val trackId: String, val pointId: String, val point: Point)

data class TrackPointRef(val trackId: String, val pointId: String)

private fun TrackPointDrag?.pointOf(trackId: String, trackPoint: TrackPoint): Point =
    if (this != null && this.trackId == trackId && pointId == trackPoint.id) point else trackPoint.point

private fun Track.positions(drag: TrackPointDrag?): List<Position> =
    points.map { drag.pointOf(id, it).coordinates }

private fun trackLineFeature(
    trackId: String,
    positions: List<Position>,
    color: Color,
    thickness: Int,
    lineStyle: LineStyle,
): Feature<LineString, JsonObject>? {
    if (positions.size < 2) return null
    return Feature(
        geometry = LineString(positions),
        properties = buildJsonObject {
            put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(trackId))
            put(MapKeys.COLOR_PROPERTY, JsonPrimitive(color.toHexRgb()))
            put(MapKeys.THICKNESS_PROPERTY, JsonPrimitive(thickness))
            put(MapKeys.LINE_STYLE_PROPERTY, JsonPrimitive(lineStyle.name))
        },
    )
}

private fun trackPointFeature(
    trackId: String,
    pointId: String,
    point: Point,
    color: Color,
): Feature<Point, JsonObject> = Feature(
    geometry = point,
    properties = buildJsonObject {
        put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(pointId))
        put(MapKeys.POINT_PARENT_ID_PROPERTY, JsonPrimitive(trackId))
        put(MapKeys.COLOR_PROPERTY, JsonPrimitive(color.toHexRgb()))
    },
)

fun Document.trackLinesGeoJson(
    draft: TrackDraft? = null,
    drag: TrackPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            tracks.forEach { track ->
                trackLineFeature(
                    trackId = track.id,
                    positions = track.positions(drag),
                    color = track.color,
                    thickness = track.lineThickness,
                    lineStyle = track.lineStyle,
                )?.let(::add)
            }
            if (draft != null) {
                trackLineFeature(
                    trackId = MapKeys.DRAFT_TRACK_ID,
                    positions = draft.points.map { it.point.coordinates },
                    color = draft.color,
                    thickness = draft.lineThickness,
                    lineStyle = draft.lineStyle,
                )?.let(::add)
            }
        }
    )
)

/** Track points are drawn only for the selected track and for the track draft. */
fun Document.trackPointsGeoJson(
    selectedObjectId: String? = null,
    draft: TrackDraft? = null,
    drag: TrackPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            tracks.filter { it.id == selectedObjectId }.forEach { track ->
                track.points.forEach { trackPoint ->
                    add(
                        trackPointFeature(
                            trackId = track.id,
                            pointId = trackPoint.id,
                            point = drag.pointOf(track.id, trackPoint),
                            color = track.color,
                        )
                    )
                }
            }
            draft?.points?.forEach { trackPoint ->
                add(
                    trackPointFeature(
                        trackId = MapKeys.DRAFT_TRACK_ID,
                        pointId = trackPoint.id,
                        point = trackPoint.point,
                        color = draft.color,
                    )
                )
            }
        }
    )
)

fun List<Feature<Geometry, JsonObject?>>.findTrackPointRef(): TrackPointRef? = firstNotNullOfOrNull { feature ->
    val properties = feature.properties ?: return@firstNotNullOfOrNull null
    val pointId = properties[MapKeys.MAPITAN_ID_PROPERTY]?.jsonPrimitive?.content ?: return@firstNotNullOfOrNull null
    val trackId =
        properties[MapKeys.POINT_PARENT_ID_PROPERTY]?.jsonPrimitive?.content ?: return@firstNotNullOfOrNull null
    TrackPointRef(trackId, pointId)
}



/** Selection highlight for the selected track and for the track draft. */
fun DocumentObject?.trackSelectionGeoJson(
    draft: TrackDraft? = null,
    drag: TrackPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            val selected = this@trackSelectionGeoJson
            if (selected is Track && selected.points.size >= 2) {
                add(Feature(geometry = LineString(selected.positions(drag)), properties = buildJsonObject { }))
            }
            val draftPositions = draft?.points?.map { it.point.coordinates }.orEmpty()
            if (draftPositions.size >= 2) {
                add(Feature(geometry = LineString(draftPositions), properties = buildJsonObject { }))
            }
        }
    )
)

/** Dashed line from the last draft point to the cursor position. */
fun TrackDraft?.draftCursorLineGeoJson(cursor: Position? = null): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            val draft = this@draftCursorLineGeoJson ?: return@buildList
            val last = draft.points.lastOrNull()?.point?.coordinates ?: return@buildList
            if (cursor == null) return@buildList
            add(
                Feature(
                    geometry = LineString(listOf(last, cursor)),
                    properties = buildJsonObject {
                        put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(MapKeys.DRAFT_TRACK_ID))
                        put(MapKeys.COLOR_PROPERTY, JsonPrimitive(draft.color.toHexRgb()))
                        put(MapKeys.THICKNESS_PROPERTY, JsonPrimitive(draft.lineThickness))
                    },
                )
            )
        }
    )
)

fun Track.insertIndexFor(position: Position): Int {
    if (points.size < 2) return points.size
    var bestIndex = 1
    var bestDistance = Double.MAX_VALUE
    points.zipWithNext().forEachIndexed { index, (start, end) ->
        val distance = distanceToSegment(position, start.point.coordinates, end.point.coordinates)
        if (distance < bestDistance) {
            bestDistance = distance
            bestIndex = index + 1
        }
    }
    return bestIndex
}

fun distanceToSegment(target: Position, start: Position, end: Position): Double {
    val px = target.longitude
    val py = target.latitude
    val ax = start.longitude
    val ay = start.latitude
    val bx = end.longitude
    val by = end.latitude
    val dx = bx - ax
    val dy = by - ay
    val lengthSquared = dx * dx + dy * dy
    val t = if (lengthSquared == 0.0) 0.0 else {
        (((px - ax) * dx + (py - ay) * dy) / lengthSquared).coerceIn(0.0, 1.0)
    }
    val cx = ax + t * dx
    val cy = ay + t * dy
    val ox = px - cx
    val oy = py - cy
    return ox * ox + oy * oy
}